import { useBreakpoint } from "@/hooks/useBreakpoint";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { useFloatingButtonsLift } from "@/components/support/useFloatingButtonsLift";
import { formatPrice } from "@/utils/money";
import { cn } from "@repo/design-system/utils";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { useCheckoutActionSlot } from "./CheckoutActionSlot";
import { useCheckoutSummarySheet } from "./CheckoutSummarySheet";

export interface CheckoutActionBarProps {
  /** The step's grand total, shown beside the action on the mobile bar. */
  total: number;
  /**
   * Provenance of that total (KAN-105), collapsed across the step's carts.
   *
   * The order summary carries the same marker, but on a phone it stacks below
   * the form while this bar is pinned - so without it the buyer confirming
   * payment sees a total with no indication of where it came from, which is
   * the moment the signal is worth most.
   */
  provenance?: SalonStockerHttpTypes.StoreLiveProvenance;
  /**
   * The step's primary action (the Continue / Pay button). From `lg` it is
   * portalled under the total in the order summary (inline at the end of the
   * step when the summary slot is unavailable); below `lg` it sits in the
   * sticky bottom bar (KAN-77). This only repositions the button - its
   * handler, props and (for Stripe) synchronous click ordering are exactly as
   * passed; the bar wraps nothing around the click and defers nothing.
   */
  primary: ReactNode;
  /**
   * Optional secondary control (e.g. Back to Cart): centred beneath the
   * primary in the order summary and to its left in the inline fallback. Not
   * rendered below `lg`: there the page carries its own back link at the end
   * of the page, after the order summary (CheckoutPage), and the pinned
   * breadcrumb's Cart step is the way back from anywhere.
   */
  secondary?: ReactNode;
  /** Applied to the inline fallback layout only. */
  className?: string;
}

/**
 * Checkout's responsive step action (KAN-77), the checkout-side twin of the
 * cart's MobileCartSummaryBar. Above `lg` the primary action sits under the
 * total in the order summary, where the cart's "Proceed to checkout" is (inline
 * at the end of the step if the summary slot is unavailable); below `lg` it
 * moves into a sticky bottom bar carrying the total, so the buyer can complete
 * the step without hunting for the button. The button itself is untouched - the
 * bar only positions it.
 */
export const CheckoutActionBar = memo(function CheckoutActionBar({
  total,
  provenance,
  primary,
  secondary,
  className
}: CheckoutActionBarProps) {
  const isDesktop = useBreakpoint("lg");
  const liftRef = useFloatingButtonsLift<HTMLDivElement>();

  const slot = useCheckoutActionSlot();
  const summarySheet = useCheckoutSummarySheet();
  const [expanded, setExpanded] = useState(false);
  // The bar needs both the lift hook's callback ref (the FABs ride above it)
  // and an object ref of its own for the open sheet's focus trap.
  const barRef = useRef<HTMLDivElement | null>(null);
  const setBarRef = useCallback(
    (node: HTMLDivElement | null) => {
      barRef.current = node;
      liftRef(node);
    },
    [liftRef]
  );
  const toggleRef = useRef<HTMLButtonElement>(null);
  const grabberRef = useRef<HTMLButtonElement>(null);

  // Closing hands focus back to the toggle that opened the sheet, so a keyboard
  // user is not dropped at the top of the page when the sheet goes.
  const close = useCallback(() => {
    setExpanded(false);
    toggleRef.current?.focus();
  }, []);

  // Open, the bar is a modal dialog: focus moves into the sheet, Escape closes
  // it, and Tab stays inside the bar (the sheet, the toggle and the step's
  // action) instead of wandering into the dimmed page behind it.
  useEffect(() => {
    if (!expanded) return;
    grabberRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const bar = barRef.current;
      const focusable = bar?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!bar || !focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!bar.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [expanded, close]);

  if (isDesktop && slot) {
    // Portalled, so the React tree (and with it every handler, context and the
    // Stripe click ordering) is unchanged - only the DOM position moves. A
    // `type="submit"` primary therefore needs its `form` attribute, as it now
    // sits outside its <form> element.
    return createPortal(
      <div className="flex flex-col gap-3 px-6 pb-4">
        <div className="flex [&>button]:w-full">{primary}</div>
        {secondary && <div className="flex justify-center">{secondary}</div>}
      </div>,
      slot
    );
  }

  if (isDesktop) {
    return (
      <div
        className={cn(
          "flex items-center",
          secondary ? "justify-between" : "justify-end",
          className
        )}
      >
        {secondary}
        {primary}
      </div>
    );
  }

  const totalLabel = (
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        Total
        <LiveProvenanceBadge
          provenance={provenance}
          subject="total"
          source="your suppliers"
          className="flex-shrink-0"
        />
      </span>
      <span className="block text-base font-bold text-gray-900">
        {formatPrice(total)}
      </span>
    </span>
  );

  return (
    <>
      {/* Scrim behind the expanded sheet - tap anywhere off it to close. Not in
          the accessibility tree: the sheet's grabber is the named way out, so
          this would only add a second, unlabelled-looking dismiss control. */}
      {expanded && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-black/30"
          onClick={close}
        />
      )}

      {/* No height spacer here: on a phone the order summary stacks after this
          form, so the last thing on the page is the summary, not this bar. The
          page reserves the bar's height at its own bottom (CheckoutPage's
          `pb-24`), which a spacer wedged mid-form couldn't do.

          The primary action keeps its position among its siblings whether or
          not the sheet is open (the conditional slots before it render `null`
          when closed), so opening the summary never remounts the button. */}
      <div
        ref={setBarRef}
        data-testid="checkout-action-bar"
        // While the sheet is open the bar is the modal: everything behind the
        // scrim is out of reach, and the step's action stays inside it.
        role={expanded ? "dialog" : undefined}
        aria-modal={expanded ? true : undefined}
        aria-label={expanded ? "Order summary details" : undefined}
        // The support FABs ride above this bar rather than over its right edge.
        className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.2)]"
      >
        {/* The same roll-up as the summary card at the end of the page - as the
            cart's bar does - without losing your place in the form. */}
        {summarySheet && expanded && (
          <div className="max-h-[60vh] overflow-y-auto border-b border-gray-100 p-3">
            {/* A grabber-style way out at the top of the sheet: the bar below
                still toggles it, but from inside the open sheet the small
                chevron down at the bottom edge is far harder to find. */}
            <button
              ref={grabberRef}
              type="button"
              onClick={close}
              aria-label="Close order summary"
              className="group -mt-1 mb-1 flex w-full flex-col items-center gap-0.5 rounded py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              <span className="h-1 w-9 rounded-full bg-gray-300 transition-colors group-hover:bg-gray-400" />
              <ChevronDown
                className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600"
                aria-hidden="true"
              />
            </button>
            {summarySheet}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-4 py-3">
          {summarySheet ? (
            <button
              ref={toggleRef}
              type="button"
              onClick={() => (expanded ? close() : setExpanded(true))}
              aria-expanded={expanded}
              // The visible "Total $X" is part of the name, so a voice-control
              // user saying "Total" reaches it.
              aria-label={`${expanded ? "Hide" : "Show"} order summary, total ${formatPrice(total)}`}
              className="flex min-w-0 items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              <ChevronUp
                className={cn(
                  "h-4 w-4 flex-shrink-0 text-gray-500 transition-transform",
                  expanded && "rotate-180"
                )}
                aria-hidden="true"
              />
              {totalLabel}
            </button>
          ) : (
            totalLabel
          )}
          {primary}
        </div>
      </div>
    </>
  );
});
